import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, ViewChild, inject } from '@angular/core';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { MaterialModule } from '../../../shared/material.module';
import { PyramidalShareholderService } from '../../../services/pyramidal-shareholder.service';
import { Helpers } from '../../../utilities/helpers';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { UpsertMultiYearPyramidalShareholderPyramidRequest, PyramidalShareholderYearPercentage } from '../../../models/api/upsertMultiYearPyramidalShareholderPyramidRequest';
import { HotToastService } from '@ngxpert/hot-toast';
import { MatDialogRef } from '@angular/material/dialog';
import { FormPyramidalPyramidItemComponent } from '../form-pyramidal-pyramid-item/form-pyramidal-pyramid-item.component';
import { PyramidalShareholderPyramidDto } from '../../../models/api/pyramidalShareholderPyramidDto';

@Component({
  selector: 'app-edit-pyramidal-pyramid-item',
  imports: [
    CommonModule,
    NgxSpinnerModule,
    MaterialModule,
    FormPyramidalPyramidItemComponent
  ],
  templateUrl: './edit-pyramidal-pyramid-item.component.html',
  styleUrl: './edit-pyramidal-pyramid-item.component.scss'
})
export class EditPyramidalPyramidItemComponent implements OnInit {
  @ViewChild(FormPyramidalPyramidItemComponent) formComponent!: FormPyramidalPyramidItemComponent;

  @Input() pyramidItem!: PyramidalShareholderPyramidDto;
  @Input() pyramidalShareholderId!: string;
  @Input() pyramidsByYear?: { [year: number]: PyramidalShareholderPyramidDto[] };
  @Input() dialogRef?: MatDialogRef<any>;
  @Output() formValidityChanged = new EventEmitter<boolean>();

  measurements: string[] = [];
  prefilledPercentages?: Map<number, number>;

  private pyramidalShareholderService = inject(PyramidalShareholderService);
  private spinnerService = inject(NgxSpinnerService);
  private toast = inject(HotToastService);
  private confirmationDialogService = inject(ConfirmationDialogService);

  constructor() {
    // Load years from measurements helper
    this.measurements = Helpers.measurements();
  }

  ngOnInit(): void {
    // Prepare prefilled percentages map by extracting all year entries for this entity
    this.prefilledPercentages = new Map();

    if (this.pyramidsByYear) {
      // Iterate through all years in the data
      Object.keys(this.pyramidsByYear).forEach(yearStr => {
        const year = parseInt(yearStr, 10);
        const pyramidsForYear = this.pyramidsByYear![year];

        // Find the entry that matches this pyramid entity
        const matchingEntry = pyramidsForYear.find(p => this.isPyramidMatch(p));

        if (matchingEntry && matchingEntry.percentage) {
          this.prefilledPercentages!.set(year, matchingEntry.percentage);
        }
      });
    } else {
      // Fallback: use the current pyramid item's data
      if (this.pyramidItem.fromDate && this.pyramidItem.percentage) {
        const year = new Date(this.pyramidItem.fromDate).getFullYear();
        this.prefilledPercentages.set(year, this.pyramidItem.percentage);
      }
    }
  }

  /**
   * Check if a pyramid entry matches the current entity being edited
   */
  private isPyramidMatch(pyramid: PyramidalShareholderPyramidDto): boolean {
    // Check each possible entity type
    if (this.pyramidItem.pyramidPyramidalShareholderId &&
        pyramid.pyramidPyramidalShareholderId === this.pyramidItem.pyramidPyramidalShareholderId) {
      return true;
    }
    if (this.pyramidItem.largeShareholderId &&
        pyramid.largeShareholderId === this.pyramidItem.largeShareholderId) {
      return true;
    }
    if (this.pyramidItem.companyId &&
        pyramid.companyId === this.pyramidItem.companyId) {
      return true;
    }
    if (this.pyramidItem.opbCompanyId &&
        pyramid.opbCompanyId === this.pyramidItem.opbCompanyId) {
      return true;
    }
    if (this.pyramidItem.directorId &&
        pyramid.directorId === this.pyramidItem.directorId) {
      return true;
    }
    return false;
  }

  get pyramidName(): string {
    return this.pyramidItem.pyramidPyramidalShareholderName
      || this.pyramidItem.largeShareholderName
      || this.pyramidItem.companyName
      || this.pyramidItem.opbCompanyName
      || this.pyramidItem.directorName
      || '';
  }

  onFormValidityChanged(isValid: boolean): void {
    this.formValidityChanged.emit(isValid);
  }

  onConfirm(): void {
    if (!this.formComponent.isValid()) {
      this.toast.error('Please fill in all required fields');
      return;
    }

    const dialogObservable = this.confirmationDialogService.openUpdateConfirmationDialog();

    dialogObservable.subscribe((result: boolean) => {
      if (result) {
        this.submitPyramid();
      }
    });
  }

  private submitPyramid(): void {
    const formData = this.formComponent.getFormValue();

    // Extract year-percentage pairs from the form
    const yearPercentages: PyramidalShareholderYearPercentage[] = [];
    this.measurements.forEach(measurement => {
      const percentage = formData[`directPercentage_${measurement}`];
      if (percentage !== null && percentage !== undefined && percentage !== '') {
        yearPercentages.push({
          year: parseInt(measurement, 10),
          percentage: parseFloat(percentage)
        });
      }
    });

    this.spinnerService.show();

    // Build the request object
    const request: UpsertMultiYearPyramidalShareholderPyramidRequest = {
      pyramidalShareholderId: this.pyramidalShareholderId,
      categoryId: formData.category?.id || this.pyramidItem.categoryId,
      subCategoryId: formData.subcategory?.id || this.pyramidItem.subCategoryId,
      yearPercentages: yearPercentages
    };

    // Set the appropriate entity ID based on the pyramid item type
    if (this.pyramidItem.pyramidPyramidalShareholderId) {
      request.pyramidPyramidalShareholderId = this.pyramidItem.pyramidPyramidalShareholderId;
    } else if (this.pyramidItem.largeShareholderId) {
      request.largeShareholderId = this.pyramidItem.largeShareholderId;
    } else if (this.pyramidItem.companyId) {
      request.companyId = this.pyramidItem.companyId;
    } else if (this.pyramidItem.opbCompanyId) {
      request.opbCompanyId = this.pyramidItem.opbCompanyId;
    } else if (this.pyramidItem.directorId) {
      request.directorId = this.pyramidItem.directorId;
    }

    this.pyramidalShareholderService.upsertMultiYearPyramid(request).subscribe({
      next: (pyramids) => {
        this.spinnerService.hide();
        this.toast.success('Pyramid entry updated successfully', {
          iconTheme: {
            primary: 'var(--mat-sys-primary)',
            secondary: '#FFF',
          }
        });

        // Close the dialog and return the result
        if (this.dialogRef) {
          this.dialogRef.close({ success: true, pyramids });
        }
      },
      error: (error) => {
        this.spinnerService.hide();
        console.error('Error updating pyramid entry:', error);
        this.toast.error('Failed to update pyramid entry');
      }
    });
  }
}
